import { mapGetters, mapMutations } from 'vuex';
import { planTypes, AUTO_ADD_FREE_TRIAL_BASE_PLAN_CODES, BILLING_CYCLE, AUTO_ADD_FREE_TRIAL_PLAN_TYPE_MAPS } from '@/common/constants';
import { AddOrRemoveBasePlanActionTypes } from '../constants';
import { getPlanTotalLicenseFilteredFreeTrial, getPlanTotalLicense } from '@/common/service/validate';
import { getPlanTypeName } from '@/common/service/parse/parse-product-name';
import { pageConfig } from '@/common/service/page-config';
import _ from 'lodash';
import { getDependentDefaultPbxBasePlan } from '@/common/product-preload';
import { PBX_RELATED_PLAN_TYPES } from '@/common/constants';

let previrousMessage = null;

const FreeTrialAutoAdd = {
  computed: {
    ...mapGetters(['getCurrentBasePlanInfo'])
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    mx_syncBasePlanLicenseForProFreeTrialAddonCase(license) {
      const selectedMeeting = this.getCurrentBasePlanInfo.plans[0];
      if (!selectedMeeting) return;
      const { plan_code, cartInfo, planNameKey } = selectedMeeting;
      if (cartInfo.selected_number < license) {
        previrousMessage && previrousMessage.close();
        previrousMessage = this.$message({
          type: 'info',
          duration: 6000,
          message: this.$t('billing.license_sync_base', [this.$t(planNameKey), this.title]),
          showClose: true,
        })
        this.updateSelectPlan({
          plan_code,
          cartInfo: {
            ...cartInfo,
            selected_number: license
          }
        })
      }
    }
  }
}

export { FreeTrialAutoAdd }

export default {
  data() {
    return {
      // title: '',
      msgInstance: null,
      visibleCapacityList: [],
      hasModifyCapacity: false,
    };
  },
  computed: {
    ...mapGetters([
      // 'getCurrentMeetingPlans', DO NOT use getCurrentMeetingPlans because the free trial plan be filtered in bought-plan-filter.js
      'getBoughtMeetingPlans',
      'getCurrentBasePlanInfo',
      'getSelectedPlans',
      'boughtPlans',
      'basicConfig',
      'isProFreeTrialAddAddonCase',
      'planMapFlat',
    ]),
    currentBasePlans() {
      /* [getBoughtMeetingPlans]  could be =>
        [
            {
                "type": "zoom_meeting",
                "category": "pro",
                "parent_category": "PRO",
                "plan_code": "zo_pro_monthly",
                "plan_type": 1096,
                "cartInfo": {
                    "selected_number": 1,
                    "add_time": 1713494257621,
                    "assignedCount": 1,
                    "price": 0,
                    "planExpireTime": "2024-04-25",
                    "planStartTime": "2024-04-18"
                },
                "amount": 0,
                "billing_cycle": "monthly",
                "dependBasePlanTypeList": [],
                "billingCycleDependBasePlanTypeList": [],
                "isOnlineFreeTrial": true,
            }
        ]*/
      return this.getBoughtMeetingPlans;
    },
    mx_getBoughtOrBuyingBasePlans() {
      const {
        category: basePlanCategory,
        billing_cycle: basePlanBillingCycle,
        planNameKey,
        typeNameKey,
        type: basePlanType,
        plan_code: basePlanCode,
        // FREE TRIAL flag
        isOnlineFreeTrial,
      } = this.currentBasePlans?.[0] || {}; // CMR basic to Pro, this could be undefined

      // Safely get basePlanName, handle undefined keys
      const basePlanName = (planNameKey && this.$t(planNameKey)) || (typeNameKey && this.$t(typeNameKey)) || '';

      // PRO free trial - calc currentBasePlanQuantity need to filtered the pro free trial
      const currentBasePlanQuantity = this.currentBasePlans?.[0]
        ? getPlanTotalLicenseFilteredFreeTrial(this.currentBasePlans)
        : 0;

      const actualBasePlanQuantity = getPlanTotalLicense(this.currentBasePlans || []);

      const period = pageConfig.period;
      let _basePlanBillingCycle = period || basePlanBillingCycle;

      // For Biz Plus free trial upgrade scenario, the base plan billing cycle should be the same as the current billing cycle
      if (basePlanType === planTypes.ZOOM_ONE_BUNDLE && isOnlineFreeTrial) {
        _basePlanBillingCycle = basePlanBillingCycle;
      }

      if (![BILLING_CYCLE.ANNUAL, BILLING_CYCLE.MONTHLY].includes(_basePlanBillingCycle)) {
        _basePlanBillingCycle = BILLING_CYCLE.ANNUAL
      }

      return {
        basePlanCategory,
        basePlanBillingCycle: _basePlanBillingCycle,
        basePlanType,
        basePlanName,
        basePlanCode,
        planNameKey,
        typeNameKey,
        currentBasePlanQuantity,
        actualBasePlanQuantity,
        isOnlineFreeTrial,
      };
    },
    mx_planLicenseCount() {
      // could be override by specific requirement
      return this.getSelectedPlans[0]?.cartInfo?.selected_number || 0;
    },
    mx_canAutoAddForFreeTrialUpgrade() {
      const { isOnlineFreeTrial = false } = this.mx_getBoughtOrBuyingBasePlans;
      const autoAddBasePlans = this.currentBasePlans.filter((plan) =>
        AUTO_ADD_FREE_TRIAL_BASE_PLAN_CODES.includes(plan.plan_code) ||
        AUTO_ADD_FREE_TRIAL_PLAN_TYPE_MAPS.includes(plan.type)
      );
      return (isOnlineFreeTrial && autoAddBasePlans.length === 1);
    }
  },
  methods: {
    ...mapMutations([
      'updateSelectPlanByCategory',
      'setUpdateBasePlan',
      'addOrRemoveBasePlan',
      'setAddedTrialToPaidBasePlanList',
      'updateSelectPlan'
    ]),
    mx_autoSyncSelectedNumber(basePlanCategory, basePlanBillingCycle, basePlanNewSelectorNumber) {
      this.updateSelectPlanByCategory({
        category: basePlanCategory,
        billingCycle: basePlanBillingCycle,
        cartInfo: { selected_number: basePlanNewSelectorNumber },
      });
      this.$nextTick(() => {
        this.setUpdateBasePlan({ update: true, from: this.title }); // Ensure the triggering of the watch in ZOOM_MEETING/edit.
      });
    },
    mx_onAddedBasePlan() {
      // no-op
    },
    mx_onRemovedBasePlan() {
      // no-op
    },
    mx_handleExceedFreeTrialBasePlanQuantity({
      isOnlineFreeTrial,
      basePlanCode,
      basePlanType,
      basePlanCategory,
      basePlanBillingCycle,
      basePlanName,
      basePlanQuantity,
    }) {
      if (isOnlineFreeTrial) {
        // FREE TRIAL - padding the paid base plan types
        this.setAddedTrialToPaidBasePlanList([basePlanCode])
      }

      const hasBasePlan = _.chain(this.getSelectedPlans)
        .map((e) => planTypes[e.type])
        .includes(planTypes[basePlanType])
        .value();

      if (!hasBasePlan) {
        this.addOrRemoveBasePlan({
          actionType: AddOrRemoveBasePlanActionTypes.ADD_PAID_BASE_PLAN_FOR_FREE_TRIAL_BASE_PLAN,
          planType: planTypes[basePlanType],
        });
        // If auto replenish selected number to base plan
        this.mx_autoSyncSelectedNumber(basePlanCategory, basePlanBillingCycle, basePlanQuantity);
        // If the base plan is a pbx related plan, add the dependent default pbx base plan
        if (_.includes(PBX_RELATED_PLAN_TYPES, basePlanType)) {
          const paygPbxBasePlan = getDependentDefaultPbxBasePlan(this.planMapFlat, this.boughtPlans);
          if (paygPbxBasePlan) {
            this.updateSelectPlan({
              plan_code: paygPbxBasePlan?.plan_code,
              cartInfo: { selected_number: 1 },
              billing_cycle: BILLING_CYCLE.PAY_AS_YOU_GO
            });
          }
        }

        this.mx_onAddedBasePlan(basePlanName);
      }
    },
  },
  created() {
    console.log('freetrial-baseplan-replenish')
    window.__this = this
    if (this.isProFreeTrialAddAddonCase) {
      const planName = this.$t(getPlanTypeName(this.$context?.type));
      const pageName = this.$context?.is?.modal ? 'modal' : 'page';

      const trackingInfo = {
        bannerLevel: `banner - product ${pageName} level`,
        message: this.$t('billing.pro_free_trial.add_on_tips', { planName }),
        uiLocation: `${planName} checkout ${pageName}`,
      }

      this.$tracking.buyInteractionWithOptions(
        trackingInfo.bannerLevel,
        `${planName} - ${trackingInfo.message} - impression`,
        undefined,
        {
          uiLocation: trackingInfo.uiLocation,
        }
      );

      this.$message({
        message: this.$t('billing.pro_free_trial.add_on_tips', { planName: this.$t(getPlanTypeName(this.$context.type)) }),
        type: 'warning',
        duration: 0,
        showClose: true,
        onClose: () => {
          this.$tracking.buyInteractionWithOptions(
            trackingInfo.bannerLevel,
            `${planName} - ${trackingInfo.message} - close`,
            undefined,
            {
              uiLocation: trackingInfo.uiLocation,
            }
          );
        }
      });
    }
  },
  watch: {
    mx_planLicenseCount: {
      immediate: true,
      handler(nv) {
        if (!this.mx_canAutoAddForFreeTrialUpgrade) return;
        const {
          basePlanCategory,
          basePlanBillingCycle,
          basePlanType,
          basePlanName,
          basePlanCode,
          currentBasePlanQuantity,
          actualBasePlanQuantity,
          isOnlineFreeTrial,
        } = this.mx_getBoughtOrBuyingBasePlans;

        // For Biz Plus free trial upgrade scenario, the base plan billing cycle should be the same as the current billing cycle
        if (
          basePlanType === planTypes.ZOOM_ONE_BUNDLE
          && this.basicConfig?.pendingFreeTrialCoupon
        ) {
          return this.mx_handleExceedFreeTrialBasePlanQuantity({
            isOnlineFreeTrial,
            basePlanCode,
            basePlanType,
            basePlanCategory,
            basePlanBillingCycle,
            basePlanName,
            basePlanQuantity: actualBasePlanQuantity,
          });
        }

        if (nv > currentBasePlanQuantity) {
          this.mx_handleExceedFreeTrialBasePlanQuantity({
            isOnlineFreeTrial,
            basePlanCode,
            basePlanType,
            basePlanCategory,
            basePlanBillingCycle,
            basePlanName,
            basePlanQuantity: nv,
            });
        } else if (nv <= currentBasePlanQuantity && this.getSelectedPlans.map((e) => planTypes[e.type]).includes(planTypes[basePlanType])) {
          this.addOrRemoveBasePlan({
            actionType: AddOrRemoveBasePlanActionTypes.REMOVE_PAID_BASE_PLAN_FOR_FREE_TRIAL_BASE_PLAN,
            planType: planTypes[basePlanType],
          });
          this.mx_onRemovedBasePlan(basePlanName);
        }
      },
    },
  },
};
